import React, { useMemo } from 'react';

/**
 * Salon-style radar (matches enterprise skin analysis):
 * circular rings, blue outline polygon, axis dots, label + score outside.
 */
const RadarChart = ({ data, size = 380 }) => {
  const model = useMemo(() => {
    const categories = Object.keys(data || {});
    const values = Object.values(data || {}).map((v) => Math.max(0, Math.min(100, Number(v) || 0)));
    const n = categories.length;
    if (!n) return null;
    const cx = size / 2;
    const cy = size / 2;
    // Leave generous margin so long labels stay visible
    const radius = size * 0.28;
    const labelDistance = 28;
    const levels = [20, 40, 60, 80, 100];

    const axis = categories.map((label, i) => {
      const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
      const score = values[i];
      const r = (score / 100) * radius;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      const labelAnchor = cos > 0.35 ? 'start' : cos < -0.35 ? 'end' : 'middle';
      const labelOffsetX = cos > 0.35 ? 4 : cos < -0.35 ? -4 : 0;
      return {
        label,
        score,
        angle,
        pointX: cx + cos * r,
        pointY: cy + sin * r,
        labelX: cx + cos * (radius + labelDistance),
        labelY: cy + sin * (radius + labelDistance),
        labelAnchor,
        labelOffsetX,
      };
    });

    return {
      cx,
      cy,
      radius,
      levels,
      axis,
      polygon: axis.map((a) => `${a.pointX},${a.pointY}`).join(' '),
    };
  }, [data, size]);

  if (!model) return null;

  return (
    <div className="hi-radar">
      <svg
        className="hi-radar__svg"
        viewBox={`0 0 ${size} ${size}`}
        width="100%"
        role="img"
        aria-label="Hair and scalp profile chart"
      >
        {model.levels.map((level) => {
          const r = (level / 100) * model.radius;
          return (
            <circle
              key={`lvl-${level}`}
              cx={model.cx}
              cy={model.cy}
              r={r}
              fill="none"
              stroke="rgba(100, 116, 139, 0.22)"
              strokeWidth="1"
            />
          );
        })}
        {model.axis.map((item, idx) => (
          <line
            key={`axis-${idx}`}
            x1={model.cx}
            y1={model.cy}
            x2={model.cx + Math.cos(item.angle) * model.radius}
            y2={model.cy + Math.sin(item.angle) * model.radius}
            stroke="rgba(100, 116, 139, 0.2)"
            strokeWidth="1"
          />
        ))}
        <polygon
          points={model.polygon}
          fill="rgba(37, 99, 235, 0.12)"
          stroke="rgba(37, 99, 235, 0.95)"
          strokeWidth="2.2"
          strokeLinejoin="round"
        />
        {model.axis.map((item, idx) => (
          <g key={`pt-${idx}`}>
            <circle cx={item.pointX} cy={item.pointY} r="3.8" fill="rgba(37, 99, 235, 0.95)" />
            <text
              x={item.labelX + item.labelOffsetX}
              y={item.labelY}
              textAnchor={item.labelAnchor}
              className="hi-radar__label"
            >
              {item.label}
            </text>
            <text
              x={item.labelX + item.labelOffsetX}
              y={item.labelY + 14}
              textAnchor={item.labelAnchor}
              className="hi-radar__score"
            >
              {item.score}%
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
};

export default RadarChart;
